import React from 'react';
import { StanButton, StanCard, StanTable, StanTag } from '@/components';
import { formatCurrency } from '@/lib/currency';
import { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { Invoice } from '@/lib/api/settings/types';
import { useInvoices } from '../../queries/useInvoices';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import IcDownload from '@/assets/icons/ic-download.svg';

const handleDownloadInvoice = (invoice: Invoice) => {
    window.open(invoice.pdf, '_blank');
};

export const InvoicesSection: React.FC = () => {
    const { data: invoices, isLoading: isInvoiceLoading } = useInvoices();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Define table columns for invoices
    const invoiceColumns: StanColumnDef<Invoice>[] = [
        {
            accessorKey: 'created_at',
            header: 'Date',
            cell: ({ getValue }) => {
                const timestamp = getValue() as number;
                const date = new Date(timestamp * 1000);
                return isMobile
                    ? date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                      })
                    : date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                      });
            },
            align: 'left',
            width: '25%',
        },
        {
            accessorKey: 'invoice_amount',
            header: 'Amount',
            cell: ({ getValue }) => {
                const amount = getValue() as string;
                const numericAmount = parseFloat(amount);
                return formatCurrency(numericAmount, 'USD');
            },
            align: 'left',
            width: '25%',
        },
        {
            accessorKey: 'invoice_status',
            header: 'Status',
            cell: ({ getValue }) => {
                const status = getValue() as string;
                return (
                    <StanTag
                        label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                        className={
                            status === 'paid'
                                ? 'bg-positive-subtle text-success-dark'
                                : 'bg-negative-dimmed text-negative-dark'
                        }
                    />
                );
            },
            align: 'left',
            width: '30%',
        },
        {
            id: 'actions',
            header: '',
            align: 'right',
            cell: ({ row }) => (
                <StanButton
                    name={`download-invoice-${row.original.id}`}
                    variant='outline'
                    size='xs'
                    onClick={() => handleDownloadInvoice(row.original)}
                    icon={IcDownload}
                >
                    {isMobile ? '' : 'Download'}
                </StanButton>
            ),
            width: '20%',
        },
    ];

    if (isInvoiceLoading || !invoices || invoices.length === 0) {
        return null;
    }

    return (
        <StanCard>
            <StanTable
                title='Invoices'
                columns={invoiceColumns}
                data={invoices}
                isLoading={isInvoiceLoading}
                emptyHeader='No invoices found'
                emptySubheader="You don't have any invoices yet."
            />
        </StanCard>
    );
};
